import { FC, useState } from 'react';
import { Dialog } from './Dialogs';
import ItemText from '../widgets/ItemText';
import { TextItem, useZakeke } from '@zakeke/zakeke-configurator-react';

import type { PropChangeHandler } from '../layout/Designer';

interface EditTextItem {
	guid: string;
	name: string;
	text: string;
	fillColor: string;
	fontFamily: string;
	fontWeight: string;
	fontSize: number;
	isTextOnPath: boolean;
	constraints: { [key: string]: any } | null;
}

const AddTextDialog: FC<{ onClose: () => void; onConfirm: (item: EditTextItem) => void }> = ({
	onClose,
	onConfirm
}) => {
	const { fonts, getPrintingMethodsRestrictions, defaultFontID, getFontWeights, T_static } = useZakeke();

	const textRestrictions = getPrintingMethodsRestrictions();
	
	// Trova il font di default basandosi su defaultFontID, altrimenti usa il primo font disponibile
	const getDefaultFont = () => {
		console.log('Default Font ID:', defaultFontID);
		if (defaultFontID !== null && defaultFontID !== undefined) {
			console.log("fonts:", fonts);
			const defaultFont = fonts.find((font) => (font as any).fontID === defaultFontID);
			if (defaultFont) {
				return defaultFont.name;
			}
		}
		return fonts[0].name;
	};




	// Semplificato - environment gestisce il fontWeight automaticamente
	const getInitialFontWeight = () => {
		// Usa un peso di default semplice, environment lo aggiusterà automaticamente
		return '400';
	};
	
	const [item, setItem] = useState<EditTextItem>({
		guid: '',
		name: '',
		text: T_static('Text'),
		fillColor: textRestrictions.defaultColor,
		fontFamily: getDefaultFont(),
		fontSize: 48,
		fontWeight: getInitialFontWeight(),
		isTextOnPath: false,
		constraints: null
	});

	const handleItemPropChange: PropChangeHandler = (item, prop, value) => {
		console.log(`handleItemPropChange`, value);
		let newItem = { ...item } as EditTextItem;

		switch (prop) {
			case 'text':
				newItem.text = value as string;
				break;
			case 'font-italic':
				// Get current font and check available weights
				const selectedFont = fonts.find(f => f.name === newItem.fontFamily);
				if (!selectedFont) break;
				
				const fontWeights = getFontWeights(selectedFont);
				console.log('[AddTextDialog] font-italic - Current fontWeight:', newItem.fontWeight, 'Available weights:', fontWeights);
				
				const weightData = newItem.fontWeight.split(' ');
				const currentIsBold = weightData.length > 1 ? (parseInt(weightData[1]) > 500) : (parseInt(weightData[0]) > 500);
				
				if (value) {
					// Vogliamo attivare italic
					if (currentIsBold) {
						// Bold + Italic -> verifica se italic bold è disponibile
						if (fontWeights.italicBold) {
							newItem.fontWeight = `italic ${fontWeights.italicBoldWeight}`;
							console.log('[AddTextDialog] Switching to italic bold:', newItem.fontWeight);
						} else {
							// Italic bold non disponibile, mantieni bold normale
							console.warn('[AddTextDialog] Italic bold not available, keeping bold');
							return; // Non aggiornare lo stato
						}
					} else {
						// Normal -> Italic
						if (fontWeights.italic) {
							newItem.fontWeight = `italic ${fontWeights.italicWeight}`;
							console.log('[AddTextDialog] Switching to italic:', newItem.fontWeight);
						} else {
							console.warn('[AddTextDialog] Italic not available');
							return; // Non aggiornare lo stato
						}
					}
				} else {
					// Vogliamo disattivare italic
					if (currentIsBold) {
						// Italic Bold -> Bold
						if (fontWeights.bold) {
							newItem.fontWeight = fontWeights.boldWeight.toString();
							console.log('[AddTextDialog] Switching from italic bold to bold:', newItem.fontWeight);
						} else {
							console.warn('[AddTextDialog] Bold not available');
							return;
						}
					} else {
						// Italic -> Normal
						if (fontWeights.normal) {
							newItem.fontWeight = fontWeights.normalWeight.toString();
							console.log('[AddTextDialog] Switching from italic to normal:', newItem.fontWeight);
						} else {
							console.warn('[AddTextDialog] Normal not available');
							return;
						}
					}
				}
				break;
			case 'font-bold':
				// Get current font and check available weights
				const currentSelectedFont = fonts.find(f => f.name === newItem.fontFamily);
				if (!currentSelectedFont) break;
				
				const currentFontWeights = getFontWeights(currentSelectedFont);
				console.log('[AddTextDialog] font-bold - Current fontWeight:', newItem.fontWeight, 'Available weights:', currentFontWeights);
				
				const currentWeightData = newItem.fontWeight.split(' ');
				const currentIsItalic = currentWeightData.length > 1 && currentWeightData[0] === 'italic';
				
				if (value) {
					// Vogliamo attivare bold
					if (currentIsItalic) {
						// Italic + Bold -> verifica se italic bold è disponibile
						if (currentFontWeights.italicBold) {
							newItem.fontWeight = `italic ${currentFontWeights.italicBoldWeight}`;
							console.log('[AddTextDialog] Switching to italic bold:', newItem.fontWeight);
						} else {
							// Italic bold non disponibile, mantieni italic normale
							console.warn('[AddTextDialog] Italic bold not available, keeping italic');
							return; // Non aggiornare lo stato
						}
					} else {
						// Normal -> Bold
						if (currentFontWeights.bold) {
							newItem.fontWeight = currentFontWeights.boldWeight.toString();
							console.log('[AddTextDialog] Switching to bold:', newItem.fontWeight);
						} else {
							console.warn('[AddTextDialog] Bold not available');
							return; // Non aggiornare lo stato
						}
					}
				} else {
					// Vogliamo disattivare bold
					if (currentIsItalic) {
						// Italic Bold -> Italic
						if (currentFontWeights.italic) {
							newItem.fontWeight = `italic ${currentFontWeights.italicWeight}`;
							console.log('[AddTextDialog] Switching from italic bold to italic:', newItem.fontWeight);
						} else {
							console.warn('[AddTextDialog] Italic not available');
							return;
						}
					} else {
						// Bold -> Normal
						if (currentFontWeights.normal) {
							newItem.fontWeight = currentFontWeights.normalWeight.toString();
							console.log('[AddTextDialog] Switching from bold to normal:', newItem.fontWeight);
						} else {
							console.warn('[AddTextDialog] Normal not available');
							return;
						}
					}
				}
				break;
			case 'font-color':
				newItem.fillColor = value as string;
				break;
		case 'font-family':
			newItem.fontFamily = value as string;
			
			// Check if current styles are available in new font and reset if not
			const newFont = fonts.find(f => f.name === value);
			if (newFont) {
				const newFontWeights = getFontWeights(newFont);
				const weightData = newItem.fontWeight.split(' ');
				const isItalic = weightData.length > 1 && weightData[0] === 'italic';
				const isBold = weightData.length > 1 ? (parseInt(weightData[1]) > 500) : (parseInt(weightData[0]) > 500);
				
				// getFontWeights returns provider format: {bold: boolean, italic: boolean, normalWeight: number, ...}
				const hasBold = newFontWeights.bold;
				const hasItalic = newFontWeights.italic;
				
				console.log('[AddTextDialog] Font change - was bold:', isBold, 'was italic:', isItalic);
				console.log('[AddTextDialog] New font supports - bold:', hasBold, 'italic:', hasItalic);
				
				// Reset to normal weight if current styles not available
				if ((isBold && !hasBold) || (isItalic && !hasItalic)) {
					// Use normal weight from new font
					newItem.fontWeight = newFontWeights.normalWeight.toString();
					console.log('[AddTextDialog] Reset fontWeight to:', newItem.fontWeight);
				}
			}
			break;
			case 'text-path':
				newItem.isTextOnPath = value as boolean;
				break;
		}
		console.log("new item:", newItem);
		setItem(newItem);
	};

	return (
		<Dialog
			title={T_static('Add text')}
			buttons={[{ label: T_static('Confirm'), onClick: () => onConfirm(item) }]}
		>
			<ItemText
				key={item.guid}
				item={item as TextItem}
				handleItemPropChange={handleItemPropChange}
				hideRemoveButton={true}
			/>
		</Dialog>
	);
};

export default AddTextDialog;